import React, {
  useEffect
} from 'react';

import {BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom'; 
import Header from './components/Header'
import Footer from './components/Footer'
import './App.css';
import Home from './components/Home';
import Guide from './components/Guide'
import Login from './components/Login'
import Signup from './components/Signup'
import Mypage from './components/Mypage'
import LeagueSessionPage from './components/LeagueSessionPage';
import { AuthProvider, useAuth } from './context/AuthContext'; 
import PlayerDetailPage from './components/PlayerDetailPage';
import LeagueDashboardPage from './components/Dashboard';
import PlayerListComponent from './components/PlayerList';
import PrivacyPolicy from './components/home/PrivacyPolicy';
import TermsOfService from './components/home/Terms';
import RankingPage from './components/RankingPage';
import UserRankingPage from './components/UserRankingPage';
import NaverLoginCallback from './components/NaverLoginCallback';
import KakaoLoginCallback from './components/KakaoLoginCallback';
import BettingListPage from './components/betting/BettingListPage';
import BettingDetailPage from './components/betting/BettingDetailPage';
import BettingCreatePage from './components/betting/BettingCreatePage';

const OAuthCallbackHandler: React.FC = () => {
  const { checkAuthStatus } = useAuth();
  const navigate = useNavigate();
  const location = useLocation(); // To get query params like invite_league

  useEffect(() => {
    // The backend has set the session cookie.
    // Now, update React's auth state by fetching the user.
    const handleAuthCallback = async () => {
      try {
        await checkAuthStatus(); // This will set the user in AuthContext

        // Check for any state passed back from OAuth (e.g., invite_league from query)
        const queryParams = new URLSearchParams(location.search);
        const inviteLeague = queryParams.get('invite_league');
        const originalPath = location.state?.from?.pathname || '/dashboard'; // If 'from' was passed

        if (inviteLeague) {
          // 수정 필요, invite league가 있으면 바로 apply를 하는건지?
          navigate(`/league/invite/${inviteLeague}`, { replace: true });
          
        } else {
          navigate(originalPath, { replace: true });
        }
      } catch (error) {
        console.error("Error during auth callback handling:", error);
        navigate('/login', { replace: true }); // Redirect to login on error
      }
    };

    handleAuthCallback();
  }, [checkAuthStatus, navigate, location]);

  return <div>Processing authentication...</div>; // Or a spinner
};

declare global {
  interface Window {
    pushToken?: string;
    isReactNativeWebView?: boolean;
    notificationPermission?: string;
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
    isAndroid?: boolean;
  }
}

function AppContent() {
  const { user, fetchApi } = useAuth(); 
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const inviteLeague = queryParams.get('invite_league');
    if (inviteLeague) {
        sessionStorage.setItem('inviteCode', inviteLeague);
    }
  }, [location.search]); 

  useEffect(() => {
    // React Native 앱으로부터 토큰을 수신하고 서버로 전송하는 함수
    const handleTokenFromReactNative = () => {
      // 1. window.expoPushToken이 있는지, 그리고 사용자가 로그인했는지 확인합니다.
      if (window.pushToken && user) {
        
        const pushToken = window.pushToken;

        // 2. 백엔드의 /api/my/notification 엔드포인트로 토큰을 전송합니다.
        fetchApi('/api/my/notification', {
          method: 'POST',
          body: JSON.stringify({ token: pushToken }),
        })
        .then(() => {
          delete window.pushToken;
        })
        .catch(error => {
          console.error('토큰 등록 오류:', error);
        });
      }
    };

    // 3. 커스텀 이벤트("pushTokenReceived")에 대한 리스너를 추가합니다.
    window.addEventListener("pushTokenReceived", handleTokenFromReactNative);

    // 4. 컴포넌트가 처음 로드될 때도 한 번 실행해 줍니다.
    //    (이벤트보다 토큰 주입이 먼저 일어났을 경우를 대비)
    handleTokenFromReactNative();

    // 5. 클린업(Cleanup) 함수: 컴포넌트가 사라질 때 이벤트 리스너를 제거합니다.
    return () => {
      window.removeEventListener("pushTokenReceived", handleTokenFromReactNative);
    };

  // user 객체가 변경될 때마다 이 로직을 재평가하여, 로그인 후에 토큰을 보낼 수 있도록 합니다.
  }, [user, fetchApi]); 

  return (
  <>
  
    <div className="app-container">
      <Header></Header>
      <div className="main-content">
        <React.Suspense fallback={<div>Loading Page...</div>}>
      
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login/>} />
            <Route path="/login/kakao/callback" element={<KakaoLoginCallback />} />
            <Route path="/login/naver/callback" element={<NaverLoginCallback />} />
            <Route path="/signup" element={<Signup/>} /> 
            <Route path="/guide" element={<Guide/>} />
            <Route path="/privacy" element={<PrivacyPolicy/>} />
            <Route path="/terms" element={<TermsOfService/>} />
            <Route path="/ranking" element={<RankingPage />} />
            <Route path="/user-ranking" element={<UserRankingPage />} />

            {/* OAuth Callback Route - public but leads to protected state */}
            <Route path="/auth/callback" element={<OAuthCallbackHandler />} />

            {/* Protected Routes */}
            <Route path="/my" element={<Mypage/>}></Route>
            <Route path="/dashboard" element={<LeagueDashboardPage/>} />
            <Route path="/league/session/:leagueCode" element={<LeagueSessionPage/>} />
            <Route path="/player" element={<PlayerListComponent/>} />
            <Route path="/player/:playerId" element={<PlayerDetailPage/>} />
            <Route path="/betting" element={<BettingListPage />} />
            <Route path="/betting/create" element={<BettingCreatePage />} />
            <Route path="/betting/:id" element={<BettingDetailPage />} />
           
            {/* Fallback for unmatched routes */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </React.Suspense>
      </div>
      <Footer></Footer>
      
    </div>
    <style>
            {`
                /* 전체 애플리케이션 컨테이너를 Flexbox로 설정합니다. */
                .app-container {
                    display: flex;
                    flex-direction: column; /* 자식 요소들을 수직으로 정렬합니다. */
                    min-height: 100vh; /* 컨테이너의 최소 높이를 화면 전체 높이로 설정합니다. */
                }

                /* 메인 콘텐츠 영역 스타일 */
                .main-content {
                    flex-grow: 1; /* 사용 가능한 모든 수직 공간을 차지하도록 설정합니다. */
                                  /* 이 속성으로 인해 콘텐츠가 적더라도 푸터가 아래로 밀려납니다. */
                }
            `}
            </style>
  </>
  );
}

function App() {
  return (
  <AuthProvider> {/* Wrap the entire application with AuthProvider */} 
      <Router>   
           {/* BrowserRouter for routing */}
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
